import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Collapse,
  Paper,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';

// ---------------------------------------------------------------------------
// Field names (partial, case-insensitive) whose values are highlighted in red
// ---------------------------------------------------------------------------
const HIGHLIGHT_KEYS = [
  'fehler', 'error', 'status', 'ergebnis', 'result',
  'kennzeichen', 'fin', 'vin',
  'gebuehr', 'betrag', 'amount',
  'datum', 'date', 'zeit', 'time',
  'code', 'grund', 'art', 'typ', 'type',
  'id', 'nummer', 'number',
  'text', 'meldung', 'message', 'hinweis'
];

const isHighlighted = (name: string) =>
  HIGHLIGHT_KEYS.some(k => name.toLowerCase().includes(k));

// ---------------------------------------------------------------------------
// XmlNode – recursive collapsible tree renderer
// ---------------------------------------------------------------------------
interface XmlNodeProps {
  node: Element;
  depth: number;
}

const XmlNode: React.FC<XmlNodeProps> = ({ node, depth }) => {
  const children = Array.from(node.children);
  const hasChildren = children.length > 0;
  const textContent =
    !hasChildren && node.firstChild?.nodeType === Node.TEXT_NODE
      ? node.firstChild.textContent?.trim() ?? ''
      : '';
  const attributes = Array.from(node.attributes);
  const [open, setOpen] = useState(depth < 2);

  return (
    <Box
      sx={{
        ml: depth > 0 ? 2.5 : 0,
        borderLeft: depth > 0 ? '1px dashed #ddd' : 'none',
        pl: depth > 0 ? 1 : 0
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        sx={{
          cursor: hasChildren ? 'pointer' : 'default',
          py: 0.2,
          '&:hover': hasChildren ? { bgcolor: 'action.hover', borderRadius: 1 } : {}
        }}
        onClick={() => hasChildren && setOpen(o => !o)}
      >
        <Box sx={{ width: 18, flexShrink: 0, color: 'text.disabled', display: 'flex', alignItems: 'center' }}>
          {hasChildren
            ? open
              ? <ExpandMoreIcon sx={{ fontSize: 16 }} />
              : <ChevronRightIcon sx={{ fontSize: 16 }} />
            : null}
        </Box>

        {/* Element name */}
        <Typography
          component="span"
          sx={{ fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 600, color: 'primary.dark' }}
        >
          {node.localName}
        </Typography>

        {/* Attributes */}
        {attributes.map(attr => (
          <Typography
            key={attr.name}
            component="span"
            sx={{ ml: 0.5, fontFamily: 'monospace', fontSize: '0.72rem', color: 'text.secondary' }}
          >
            {attr.name}=<span style={{ color: '#2e7d32' }}>"{attr.value}"</span>
          </Typography>
        ))}

        {/* Inline text value */}
        {textContent && (
          <Typography
            component="span"
            sx={{
              ml: 1,
              fontFamily: 'monospace',
              fontSize: '0.78rem',
              color: isHighlighted(node.localName) ? 'error.main' : 'text.primary',
              fontWeight: isHighlighted(node.localName) ? 700 : 400
            }}
          >
            {textContent}
          </Typography>
        )}
      </Box>

      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          {children.map((child, i) => (
            <XmlNode key={i} node={child} depth={depth + 1} />
          ))}
        </Collapse>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main XmlViewer component
// ---------------------------------------------------------------------------
export interface XmlViewerProps {
  content: string;
  maxHeight?: string;
}

const XmlViewer: React.FC<XmlViewerProps> = ({ content, maxHeight = '55vh' }) => {
  const [tab, setTab] = useState(0);
  const [copied, setCopied] = useState(false);

  const { doc, parseError } = useMemo(() => {
    if (!content) return { doc: null, parseError: null };
    try {
      // Strip UTF-8 BOM, surrounding whitespace, and the <?xml ?> declaration.
      // The backend sends attribute values without quotes throughout the entire document
      // (e.g. xmlns:ns2=http://... produkt=IKOL-KFZ), which the strict DOMParser rejects.
      // We quote every unquoted attribute value before handing it to DOMParser.
      const normalized = content
        .replace(/^\uFEFF/, '')
        .trim()
        .replace(/^<\?xml[^?]*\?>\s*/i, '')
        .replace(/(\s[\w:.-]+=)([^\s"'<>][^\s>]*)/g, '$1"$2"');
      const parser = new DOMParser();
      const parsed = parser.parseFromString(normalized, 'text/xml');
      const errEl = parsed.querySelector('parsererror') ?? parsed.querySelector('parseerror');
      if (errEl) {
        const msg = errEl.querySelector('div')?.textContent?.trim() ?? errEl.textContent?.trim() ?? 'Unbekannter Fehler';
        return { doc: null, parseError: `XML-Fehler: ${msg}` };
      }
      return { doc: parsed, parseError: null };
    } catch (e: any) {
      return { doc: null, parseError: `Fehler beim Verarbeiten des XML: ${e?.message ?? ''}` };
    }
  }, [content]);

  const handleCopy = () => {
    navigator.clipboard.writeText(content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };

  if (!content) {
    return (
      <Typography color="text.secondary" sx={{ p: 2 }}>
        Kein XML-Inhalt vorhanden
      </Typography>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 36 }}>
          <Tab label="Strukturansicht" sx={{ minHeight: 36, py: 0.5, fontSize: '0.8rem' }} />
          <Tab label="Roh-XML" sx={{ minHeight: 36, py: 0.5, fontSize: '0.8rem' }} />
        </Tabs>
        <Tooltip title={copied ? 'Kopiert!' : 'XML kopieren'}>
          <IconButton size="small" onClick={handleCopy}>
            <CopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Tab 0: Tree view ── */}
      {tab === 0 && (
        <Box sx={{ overflow: 'auto', maxHeight }}>
          {parseError ? (
            <Box>
              <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                {parseError}
              </Typography>
              <Box component="pre" sx={rawStyle}>
                {content}
              </Box>
            </Box>
          ) : !doc ? (
            <Box component="pre" sx={rawStyle}>
              {content}
            </Box>
          ) : (
            <Paper variant="outlined" sx={{ p: 1, bgcolor: '#fafafa' }}>
              <XmlNode node={doc.documentElement} depth={0} />
            </Paper>
          )}
        </Box>
      )}

      {/* ── Tab 1: Raw XML ── */}
      {tab === 1 && (
        <Box component="pre" sx={{ ...rawStyle, maxHeight, overflow: 'auto' }}>
          {content}
        </Box>
      )}
    </Box>
  );
};

const rawStyle = {
  backgroundColor: '#f5f5f5',
  p: 2,
  borderRadius: 1,
  fontSize: '0.72rem',
  fontFamily: 'monospace',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
  m: 0
} as const;

export default XmlViewer;
